import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
    Box,
    Typography,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    OutlinedInput,
    SelectChangeEvent
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { devLog } from '../../utils/devLog';

interface Category {
    id: number;
    name: string;
    color: string;
}

interface FileWithProgress {
    file: File;
    progress: number;
    status: 'pending' | 'uploading' | 'success' | 'error';
    error?: string;
}

interface FileUploadZoneProps {
    folderId?: number;
    onUploadComplete?: () => void;
    categories?: Category[];
}

const FileUploadZone: React.FC<FileUploadZoneProps> = ({ folderId, onUploadComplete, categories = [] }) => {
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
    const [files, setFiles] = useState<FileWithProgress[]>([]);
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();

    const uploadFile = async (fileWithProgress: FileWithProgress, index: number, categoryIds: number[]) => {
        const formData = new FormData();
        formData.append('file', fileWithProgress.file);
        if (folderId) {
            formData.append('folderId', folderId.toString());
        }

        try {
            setFiles(prev => prev.map((f, i) =>
                i === index ? { ...f, status: 'uploading', progress: 0 } : f
            ));

            // Simuliere Progress (in echter Implementierung mit XMLHttpRequest für echten Progress)
            const progressInterval = setInterval(() => {
                setFiles(prev => prev.map((f, i) =>
                    i === index && f.progress < 90
                        ? { ...f, progress: f.progress + 10 }
                        : f
                ));
            }, 200);

            const token = localStorage.getItem('token');

            // Debug logging
            devLog('Uploading file:', fileWithProgress.file.name);
            devLog('File size:', fileWithProgress.file.size);
            devLog('File type:', fileWithProgress.file.type);
            devLog('FormData entries:', Array.from(formData.entries()));

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/media/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    // Note: Do NOT set Content-Type header - browser will set it automatically with boundary
                },
                body: formData
            });

            clearInterval(progressInterval);

            if (!response.ok) {
                throw new Error('Upload failed');
            }

            const result = await response.json();
            devLog('Upload result:', result);
            // Update file with categories if any selected
            devLog('Category IDs to assign:', categoryIds);
            if (categoryIds.length > 0 && result.file) {
                const categoryIRIs = categoryIds.map(id => `/api/media_categories/${id}`);
                const fileId = result.file.id;
                devLog('Updating file with categories, fileId:', fileId);
                devLog('Category IRIs:', categoryIRIs);
                await dataProvider.update('media_files', {
                    id: fileId,
                    data: { categories: categoryIRIs },
                    previousData: result.file
                });
                devLog('Categories successfully assigned!');
            }

            setFiles(prev => prev.map((f, i) =>
                i === index ? { ...f, status: 'success', progress: 100 } : f
            ));

            notify(`${fileWithProgress.file.name} erfolgreich hochgeladen`, { type: 'success' });

            // Nach 2 Sekunden aus Liste entfernen
            setTimeout(() => {
                setFiles(prev => prev.filter((_, i) => i !== index));
                if (onUploadComplete) {
                    onUploadComplete();
                }
                refresh();
            }, 2000);

        } catch (error) {
            setFiles(prev => prev.map((f, i) =>
                i === index
                    ? { ...f, status: 'error', error: 'Upload fehlgeschlagen' }
                    : f
            ));
            notify(`Fehler beim Hochladen von ${fileWithProgress.file.name}`, { type: 'error' });
        }
    };

    const onDrop = useCallback((acceptedFiles: File[]) => {
        const newFiles: FileWithProgress[] = acceptedFiles.map(file => ({
            file,
            progress: 0,
            status: 'pending'
        }));

        setFiles(prev => [...prev, ...newFiles]);

        // Starte Uploads
        newFiles.forEach((fileWithProgress, index) => {
            uploadFile(fileWithProgress, files.length + index, selectedCategoryIds);
        });
    }, [files.length, selectedCategoryIds]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: true
    });

    const removeFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };
    const handleCategoryChange = (event: SelectChangeEvent<number[]>) => {
        const value = event.target.value;
        setSelectedCategoryIds(typeof value === 'string' ? [] : value);
    };

    return (
        <Box>
            {categories.length > 0 && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Kategorien für Upload</InputLabel>
                    <Select
                        multiple
                        value={selectedCategoryIds}
                        onChange={handleCategoryChange}
                        input={<OutlinedInput label="Kategorien für Upload" />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((categoryId) => {
                                    const category = categories.find(c => c.id === categoryId);
                                    return category ? (
                                        <Chip
                                            key={category.id}
                                            label={category.name}
                                            size="small"
                                            sx={{
                                                backgroundColor: category.color,
                                                color: 'white'
                                            }}
                                        />
                                    ) : null;
                                })}
                            </Box>
                        )}
                    >
                        {categories.map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                                <Chip
                                    label={category.name}
                                    size="small"
                                    sx={{
                                        backgroundColor: category.color,
                                        color: 'white',
                                        mr: 1
                                    }}
                                />
                                {category.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
            <Box
                {...getRootProps()}
                sx={{
                    border: '2px dashed',
                    borderColor: isDragActive ? 'primary.main' : 'grey.400',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
                    transition: 'all 0.3s',
                    '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover'
                    }
                }}
            >
                <input {...getInputProps()} />
                <CloudUploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                    {isDragActive ? 'Dateien hier ablegen...' : 'Dateien hochladen'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Ziehe Dateien hierher oder klicke zum Auswählen
                </Typography>
            </Box>

            {files.length > 0 && (
                <List sx={{ mt: 2 }}>
                    {files.map((fileWithProgress, index) => (
                        <ListItem
                            key={index}
                            secondaryAction={
                                fileWithProgress.status === 'success' ? (
                                    <CheckCircleIcon color="success" />
                                ) : fileWithProgress.status !== 'uploading' ? (
                                    <IconButton edge="end" onClick={() => removeFile(index)}>
                                        <CloseIcon />
                                    </IconButton>
                                ) : null
                            }
                        >
                            <ListItemText
                                primary={fileWithProgress.file.name}
                            />
                            {fileWithProgress.status === 'uploading' && (
                                <Box sx={{ width: '100%', mt: 1 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={fileWithProgress.progress}
                                    />
                                </Box>
                            )}
                            {fileWithProgress.status === 'error' && (
                                <Alert severity="error" sx={{ mt: 1, width: '100%' }}>
                                    {fileWithProgress.error}
                                </Alert>
                            )}
                        </ListItem>
                    ))}
                </List>
            )}
        </Box>
    );
};

export default FileUploadZone;
